/**
 * Client Fact Sheet — read-only at-a-glance view.
 *
 * Shows everything an admin / on-call manager needs in <5 seconds: critical
 * safety info (allergies, code status, fall risk) up top, then identity,
 * clinical baseline, care team, recent activity, and assigned caregivers.
 *
 * Printable: applies print-friendly styles when sent to a printer so the
 * sheet can live at the bedside as a paper backup.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
  AlertTriangle, Heart, Activity, User, Phone, Stethoscope,
  Pill, FileText, Calendar, Loader2, Shield, ScrollText, Smile, Footprints,
} from "lucide-react";
import FactSheetExtras from "@/components/admin-clients/FactSheetExtras";

const DIET_LABEL = {
  regular: "Regular", diabetic: "Diabetic", low_sodium: "Low sodium",
  soft: "Soft", pureed: "Pureed", thickened_liquids: "Thickened liquids",
  npo: "NPO", tube_feeding: "Tube feeding",
};
const MOBILITY_LABEL = {
  independent: "Independent", cane: "Cane", walker: "Walker",
  wheelchair: "Wheelchair", bedbound: "Bedbound",
};
const COGNITION_LABEL = {
  alert: "Alert", mild_impairment: "Mild impairment",
  moderate_impairment: "Moderate impairment",
  severe_impairment: "Severe impairment", non_verbal: "Non-verbal",
};
const CODE_STATUS_LABEL = {
  full_code: "Full code", dnr: "DNR", dni: "DNI",
  dnr_dni: "DNR + DNI", comfort_only: "Comfort only",
};

const SEVERITY_COLOR = {
  mild: "#22C55E", moderate: "#F59E0B",
  severe: "#EF4444", anaphylactic: "#B91C1C",
};

function calcAge(dob) {
  if (!dob) return null;
  try {
    const b = new Date(dob);
    const diff = Date.now() - b.getTime();
    return Math.floor(diff / (365.25 * 24 * 60 * 60 * 1000));
  } catch { return null; }
}

function Badge({ children, color = "#64748B", subtle = false }) {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium"
      style={{
        background: subtle ? color + "1a" : color,
        color: subtle ? color : "white",
      }}
    >
      {children}
    </span>
  );
}

function Section({ icon: Icon, title, children, danger = false, accent = "#0F172A" }) {
  return (
    <div
      className="card-surface p-4"
      style={danger ? { borderColor: "#FCA5A5", background: "#FEF2F2" } : {}}
    >
      <div className="mb-3 flex items-center gap-2">
        <Icon size={16} style={{ color: danger ? "#B91C1C" : accent }} />
        <h3
          className="text-sm font-semibold uppercase tracking-wide"
          style={{ color: danger ? "#7F1D1D" : "#0F172A" }}
        >
          {title}
        </h3>
      </div>
      {children}
    </div>
  );
}

export default function ClientFactSheet({ client, caregiverLookup }) {
  const [recent, setRecent] = useState({
    lastReport: null, lastVitals: null, lastMood: null,
    openIncidents: 0, loading: true,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [reports, vitals, mood, incidents] = await Promise.all([
          api.get(`/reports?client_id=${client.id}&limit=1`).catch(() => ({ data: [] })),
          api.get(`/clients/${client.id}/vitals?days=14`).catch(() => ({ data: [] })),
          api.get(`/clients/${client.id}/mood?days=14`).catch(() => ({ data: [] })),
          api.get(`/clients/${client.id}/incidents?days=30`).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setRecent({
          lastReport: reports.data[0] || null,
          lastVitals: vitals.data[0] || null,
          lastMood: mood.data[0] || null,
          openIncidents: (incidents.data || []).filter((i) => !i.reviewed_at).length,
          loading: false,
        });
      } catch {
        if (!cancelled) setRecent((r) => ({ ...r, loading: false }));
      }
    })();
    return () => { cancelled = true; };
  }, [client.id]);

  const age = useMemo(() => calcAge(client.dob), [client.dob]);
  const allergies = (client.allergies || []).filter((a) => (a.substance || "").trim());
  const anaphylactic = allergies.some((a) => a.severity === "anaphylactic" || a.severity === "severe");
  const assignedCaregivers = (client.assigned_caregiver_ids || [])
    .map((id) => caregiverLookup?.[id])
    .filter(Boolean);

  return (
    <div className="space-y-4" data-testid="client-fact-sheet">
      {/* --- Header --- */}
      <div className="card-surface p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-semibold text-clinical-text">{client.name}</h2>
              {client.pronouns && (
                <span className="text-xs text-clinical-subtext">({client.pronouns})</span>
              )}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-clinical-subtext">
              {age !== null && <span>Age {age}</span>}
              {client.dob && <span>· DOB {client.dob}</span>}
              {client.gender && (
                <span className="capitalize">· {client.gender.replace("_", " ")}</span>
              )}
              {client.address && <span className="block w-full text-xs sm:inline sm:w-auto">📍 {client.address}</span>}
            </div>
          </div>
          {/* Status badges */}
          <div className="flex flex-wrap gap-1.5">
            {client.code_status && (
              <Badge color={client.code_status === "dnr" || client.code_status === "dnr_dni" ? "#B91C1C" : "#0EA5E9"}>
                <Shield size={11} /> {CODE_STATUS_LABEL[client.code_status] || client.code_status}
              </Badge>
            )}
            {client.fall_risk === "high" && (
              <Badge color="#EF4444"><Footprints size={11} /> HIGH FALL RISK</Badge>
            )}
            {client.fall_risk === "moderate" && (
              <Badge color="#F59E0B"><Footprints size={11} /> Mod. fall risk</Badge>
            )}
            {client.meds_locked && (
              <Badge color="#7C3AED" subtle><Pill size={11} /> Meds locked</Badge>
            )}
          </div>
        </div>

        {/* Diagnosis on its own line — high prominence */}
        {client.primary_diagnosis && (
          <div className="mt-3 rounded-lg border border-clinical-border bg-clinical-bg/40 p-2.5">
            <div className="label-eyebrow mb-1">Primary diagnosis</div>
            <div className="text-sm text-clinical-text">{client.primary_diagnosis}</div>
          </div>
        )}
      </div>

      {/* --- Critical safety: Allergies --- */}
      <Section
        icon={AlertTriangle}
        title={`Allergies${allergies.length ? ` (${allergies.length})` : ""}`}
        danger={anaphylactic}
      >
        {allergies.length === 0 ? (
          <p className="text-sm text-clinical-subtext">No known allergies on file.</p>
        ) : (
          <ul className="space-y-2">
            {allergies.map((a, i) => (
              <li key={i} className="flex items-start justify-between gap-3 rounded-lg bg-white/70 p-2.5">
                <div>
                  <div className="font-medium text-clinical-text">{a.substance}</div>
                  {a.reaction && <div className="text-xs text-clinical-subtext">{a.reaction}</div>}
                </div>
                <Badge color={SEVERITY_COLOR[a.severity] || "#64748B"}>
                  {a.severity}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* --- Clinical baseline strip --- */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatusCard
          icon={Footprints} label="Mobility"
          value={client.mobility ? (MOBILITY_LABEL[client.mobility] || client.mobility) : "—"}
        />
        <StatusCard
          icon={Activity} label="Cognition"
          value={client.cognition ? (COGNITION_LABEL[client.cognition] || client.cognition) : "—"}
        />
        <StatusCard
          icon={FileText} label="Diet"
          value={client.diet ? (DIET_LABEL[client.diet] || client.diet) : "—"}
          sub={client.diet_notes}
        />
        <StatusCard
          icon={ScrollText} label="Advance directive"
          value={client.advance_directive_on_file ? "On file" : "Not on file"}
          accent={client.advance_directive_on_file ? "#16A34A" : "#94A3B8"}
        />
      </div>

      {/* --- Recent activity stripe --- */}
      <Section icon={Calendar} title="Recent activity (last 14-30 days)">
        {recent.loading ? (
          <div className="flex items-center gap-2 text-sm text-clinical-subtext">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <ActivityCard
              icon={FileText} label="Last shift report"
              value={recent.lastReport
                ? `${recent.lastReport.shift_date} · ${recent.lastReport.shift_type}`
                : "None"}
              sub={recent.lastReport?.caregiver_name}
            />
            <ActivityCard
              icon={Heart} label="Last vitals"
              value={recent.lastVitals
                ? formatVitalsSummary(recent.lastVitals)
                : "None"}
              sub={recent.lastVitals
                ? new Date(recent.lastVitals.recorded_at).toLocaleDateString()
                : ""}
            />
            <ActivityCard
              icon={Smile} label="Last mood"
              value={recent.lastMood
                ? `${recent.lastMood.shift} · ${labelMood(recent.lastMood.rating)}`
                : "None"}
              sub={recent.lastMood
                ? new Date(recent.lastMood.recorded_at).toLocaleDateString()
                : ""}
            />
            <ActivityCard
              icon={AlertTriangle} label="Open incidents"
              value={String(recent.openIncidents)}
              accent={recent.openIncidents > 0 ? "#B91C1C" : "#64748B"}
            />
          </div>
        )}
      </Section>

      {/* --- Care team --- */}
      <Section icon={Stethoscope} title="Care team">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <ContactCard
            label="Physician"
            name={client.doctor_name || "—"}
            phone={client.doctor_phone}
            icon={Stethoscope}
          />
          <ContactCard
            label="Family"
            name={client.family_contact_name || "—"}
            phone={client.family_contact_phone}
            icon={User}
          />
        </div>
        {client.emergency_note && (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs">
            <div className="label-eyebrow mb-1 text-amber-900">Emergency note</div>
            <p className="text-amber-900">{client.emergency_note}</p>
          </div>
        )}
      </Section>

      {/* --- Assigned caregivers --- */}
      {assignedCaregivers.length > 0 && (
        <Section icon={User} title={`Assigned caregivers (${assignedCaregivers.length})`}>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            {assignedCaregivers.map((cg) => (
              <div key={cg.id} className="flex items-center justify-between rounded-lg bg-white/70 p-2.5">
                <div>
                  <div className="font-medium text-clinical-text">{cg.name}</div>
                  <div className="text-xs text-clinical-subtext">{cg.email}</div>
                </div>
                {cg.phone && (
                  <a
                    href={`tel:${cg.phone}`}
                    className="text-xs text-clinical-primary hover:underline"
                  >
                    {cg.phone}
                  </a>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* --- Scheduled days --- */}
      {(client.scheduled_days || []).length > 0 && (
        <Section icon={Calendar} title="Schedule">
          <div className="flex flex-wrap gap-1.5">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
              <span
                key={d}
                className={`rounded-md px-2 py-1 text-xs font-medium ${
                  client.scheduled_days.includes(d)
                    ? "bg-clinical-primary text-white"
                    : "bg-clinical-bg text-clinical-subtext"
                }`}
              >
                {d}
              </span>
            ))}
          </div>
        </Section>
      )}

      {/* Free-form notes (still useful for anything not captured above) */}
      {client.notes && (
        <Section icon={FileText} title="Notes">
          <p className="whitespace-pre-line text-sm text-clinical-text">{client.notes}</p>
        </Section>
      )}

      {/* --- Extended read-only blocks: risk, body check trend, strengths,
          client info, consents (with PDF packet download) --- */}
      <FactSheetExtras clientId={client.id} clientName={client.name} />
    </div>
  );
}

function StatusCard({ icon: Icon, label, value, sub, accent = "#0F172A" }) {
  return (
    <div className="card-surface p-3">
      <div className="flex items-center gap-2 text-xs text-clinical-subtext">
        <Icon size={13} style={{ color: accent }} />
        <span className="uppercase tracking-wide">{label}</span>
      </div>
      <div className="mt-1 text-sm font-medium text-clinical-text">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-clinical-subtext">{sub}</div>}
    </div>
  );
}

function ActivityCard({ icon: Icon, label, value, sub, accent = "#0F172A" }) {
  return (
    <div className="rounded-lg border border-clinical-border bg-white p-3">
      <div className="flex items-center gap-2 text-xs text-clinical-subtext">
        <Icon size={13} style={{ color: accent }} />
        <span className="uppercase tracking-wide">{label}</span>
      </div>
      <div className="mt-1 text-sm font-medium" style={{ color: accent }}>{value}</div>
      {sub && <div className="mt-0.5 text-xs text-clinical-subtext">{sub}</div>}
    </div>
  );
}

function ContactCard({ icon: Icon, label, name, phone }) {
  return (
    <div className="rounded-lg border border-clinical-border bg-white p-3">
      <div className="flex items-center gap-2 text-xs text-clinical-subtext">
        <Icon size={13} />
        <span className="uppercase tracking-wide">{label}</span>
      </div>
      <div className="mt-1 text-sm font-medium text-clinical-text">{name}</div>
      {phone && (
        <a href={`tel:${phone}`} className="mt-0.5 inline-flex items-center gap-1 text-xs text-clinical-primary hover:underline">
          <Phone size={11} /> {phone}
        </a>
      )}
    </div>
  );
}

function formatVitalsSummary(v) {
  const parts = [];
  if (v.systolic && v.diastolic) parts.push(`${v.systolic}/${v.diastolic}`);
  if (v.pulse) parts.push(`${v.pulse} bpm`);
  if (v.temp_f) parts.push(`${v.temp_f}°F`);
  if (v.o2_sat) parts.push(`${v.o2_sat}%`);
  return parts.join(" · ") || "—";
}

function labelMood(rating) {
  if (rating >= 2) return "Agitated";
  if (rating === 1) return "Restless";
  if (rating === 0) return "Calm";
  if (rating === -1) return "Low";
  return "Withdrawn";
}
